import { OWNER_ROLE_KEY, PERMISSION_MODULES, isPermissionCode } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Alert, Badge, Button, Card, Field, Input, PageTitle, Spinner } from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";

type Role = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  activeMembers: number;
};
type Permission = { code: string; module: string; description: string; isSensitive: boolean };
type Draft = {
  id: string | null;
  name: string;
  description: string;
  permissions: string[];
  key: string;
};

/**
 * Papéis são conjuntos de permissões por ação. Criar ou alterar um papel não exige
 * mudar o sistema: basta escolher as permissões do catálogo.
 */
export function RolesPage() {
  const can = useCan();
  const manage = can("admin.papeis.gerenciar");
  const queryClient = useQueryClient();
  const roles = useQuery({ queryKey: ["papeis"], queryFn: () => apiFetch<Role[]>("/papeis") });
  const catalog = useQuery({
    queryKey: ["permissoes"],
    queryFn: () => apiFetch<Permission[]>("/permissoes"),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [toRemove, setToRemove] = useState<Role | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["papeis"] });
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { name: d.name, description: d.description, permissions: d.permissions };
      return d.id
        ? apiFetch(`/papeis/${d.id}`, { method: "PUT", body })
        : apiFetch("/papeis", { method: "POST", body, idempotencyKey: d.key });
    },
    onSuccess: async () => {
      setDraft(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/papeis/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  if (roles.isPending || catalog.isPending) return <Spinner />;
  if (roles.isError) return <Alert tone="error">{errorMessage(roles.error)}</Alert>;
  if (catalog.isError) return <Alert tone="error">{errorMessage(catalog.error)}</Alert>;

  const byModule = Object.keys(PERMISSION_MODULES).map((m) => ({
    module: m,
    label: PERMISSION_MODULES[m] ?? m,
    items: catalog.data.filter((p) => p.module === m),
  }));
  const describe = (code: string) => catalog.data.find((p) => p.code === code)?.description ?? code;

  return (
    <div className="space-y-6">
      <PageTitle
        title="Papéis e permissões"
        description="Cada papel é um conjunto de permissões por ação. Você só concede permissões que também possui."
        actions={
          manage && (
            <Button
              onClick={() =>
                setDraft({
                  id: null,
                  name: "",
                  description: "",
                  permissions: [],
                  key: newIdempotencyKey(),
                })
              }
            >
              Novo papel
            </Button>
          )
        }
      />
      {remove.isError && <Alert tone="error">{errorMessage(remove.error)}</Alert>}

      {draft && (
        <Card
          title={draft.id ? "Editar papel" : "Novo papel"}
          actions={
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Cancelar
            </Button>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome">
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label="Descrição">
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </Field>
          </div>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {byModule.map((g) => (
              <fieldset key={g.module}>
                <legend className="mb-1 text-sm font-semibold">{g.label}</legend>
                {g.items.map((p) => (
                  <label key={p.code} className="flex items-start gap-2 py-0.5 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={draft.permissions.includes(p.code)}
                      disabled={!(isPermissionCode(p.code) && can(p.code))}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          permissions: e.target.checked
                            ? [...draft.permissions, p.code]
                            : draft.permissions.filter((c) => c !== p.code),
                        })
                      }
                    />
                    <span>
                      {p.description} {p.isSensitive && <Badge tone="warn">sensível</Badge>}
                    </span>
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
          {save.isError && (
            <div className="mt-4">
              <Alert tone="error">{errorMessage(save.error)}</Alert>
            </div>
          )}
          <Button className="mt-5" disabled={save.isPending} onClick={() => save.mutate(draft)}>
            Salvar papel
          </Button>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {roles.data.map((r) => (
          <Card
            key={r.id}
            title={r.name}
            actions={
              <div className="flex items-center gap-2">
                {r.isSystem && <Badge>padrão</Badge>}
                <Badge>{r.activeMembers} usuário(s)</Badge>
                {manage && r.key !== OWNER_ROLE_KEY && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setDraft({
                        id: r.id,
                        name: r.name,
                        description: r.description ?? "",
                        permissions: r.permissions,
                        key: newIdempotencyKey(),
                      })
                    }
                  >
                    Editar
                  </Button>
                )}
                {manage && !r.isSystem && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => setToRemove(r)}
                  >
                    Excluir
                  </Button>
                )}
              </div>
            }
          >
            {r.description && <p className="mb-2 text-sm text-muted-foreground">{r.description}</p>}
            <ul className="grid gap-x-4 text-sm sm:grid-cols-2">
              {r.permissions.map((c) => (
                <li key={c}>· {describe(c)}</li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
      <ConfirmDialog
        open={toRemove !== null}
        title={`Excluir o papel ${toRemove?.name ?? ""}?`}
        confirmLabel="Excluir papel"
        destructive
        busy={remove.isPending}
        onCancel={() => setToRemove(null)}
        onConfirm={() => {
          if (toRemove) remove.mutate(toRemove.id);
          setToRemove(null);
        }}
      >
        O papel deixa de existir. Quem só tinha este papel perde as permissões dele. A exclusão não
        pode ser desfeita.
      </ConfirmDialog>
    </div>
  );
}
